import type { Route } from '~/apps/pu-links/routeHelpers'
import { getPathElements } from '~/apps/pu-links/getPathElements'
import { SearchFilters } from '../schemas'
import { isValidSortOption } from '~/utility/isValidSortOption'
import { logger } from '~/utility/logger'
import { localePath } from '~/utility/route'
import { LOCALE_CODES } from '~/lang/locales'

interface RedirectResult {
  redirect: boolean
  status?: number
  url?: string
}

interface CheckPathDependencies {
  route: Route
  filters: SearchFilters
  facetsBySlug: (slug: string) => any
  langCode: string
  router: any
}

/**
 * Helper function to check if a path is a search route, accounting for language prefixes
 */
function isSearchPath(path: string): boolean {
  // Remove language prefix if present
  let pathWithoutLang = path
  for (const locale of LOCALE_CODES) {
    if (path.startsWith(`/${locale}/`)) {
      pathWithoutLang = path.substring(`/${locale}`.length)
      break
    }
  }
  // Check if the path (with or without lang prefix) starts with /search/
  return pathWithoutLang.startsWith('/search/')
}

/**
 * Checks the search path for various redirect conditions
 * Returns redirect information if a redirect is needed
 */
export function checkSearchPath({
  route,
  filters,
  facetsBySlug,
  langCode,
  router,
}: CheckPathDependencies): RedirectResult {
  const path = route.path
  const sortParam = route.query.sort

  // Check for invalid URL pattern with encoded slashes
  if (/-\/.*%2F/.test(route.path)) {
    logger('Invalid URL pattern detected', route.fullPath)
    return { redirect: true, status: 404 }
  }

  // Check for empty facet path
  if (path.endsWith('/-/')) {
    const url = route.fullPath.replace(/-\//, '')
    logger('SearchMain redirect empty slash', url)
    return { redirect: true, status: 301, url }
  }

  // Check for multiple '-/' in the URL
  if ((route.fullPath.match(/-\//g) || []).length > 1) {
    logger('Multiple empty slashes found', route.fullPath)
    return { redirect: true, status: 404 }
  }

  // Validate the 'sort' query parameter
  if (sortParam && !isValidSortOption(sortParam as string)) {
    logger('Invalid sort parameter', sortParam)
    return { redirect: true, status: 404 }
  }

  // Validate facet slugs
  const invalidFacets =
    filters.facets?.filter((slug) => !facetsBySlug(slug)) || []
  if (invalidFacets.length) {
    logger('Invalid facets detected', {
      invalidFacets,
      path: route.fullPath,
      allFacets: filters.facets,
    })
    return { redirect: true, status: 404 }
  }

  // Check for 404 path
  if (route.path.startsWith('/404/')) {
    logger('Redirecting to 404 for path', path)
    return { redirect: true, status: 404 }
  }

  // Check for multiple facets - if more than 1 facet, must use /search/ URL with query params
  if (
    filters.facets &&
    filters.facets.length > 1 &&
    !isSearchPath(route.path)
  ) {
    const [hierarchyPath] = getPathElements(route)

    // Build query parameters
    const query: Record<string, any> = {
      ...route.query,
      facet: filters.facets,
    }

    // Add type parameter from the base/category
    if (filters.categoryIds?.length) {
      query.type = filters.categoryIds
    }

    // Remove the base query param if it exists (we don't use it in non-SEO URLs)
    delete query.base

    // Build the non-SEO URL path
    const nonSeoPath = hierarchyPath ? `/search/${hierarchyPath}` : '/search/'

    // Build query string
    const queryString = new URLSearchParams()
    Object.entries(query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => queryString.append(key, v))
      } else if (value !== undefined && value !== null) {
        queryString.append(key, value)
      }
    })

    // Use localePath to handle language codes properly
    const url = localePath(
      `${nonSeoPath}${queryString.toString() ? '?' + queryString.toString() : ''}`,
      langCode,
      router,
    )

    // Prevent infinite redirect loop
    if (route.fullPath === url) {
      logger('Redirect URL matches current URL, skipping redirect', {
        url: route.fullPath,
        facets: filters.facets,
      })
      return { redirect: false }
    }

    logger('Redirecting multiple facets to non-SEO URL', {
      from: route.fullPath,
      to: url,
      facets: filters.facets,
    })
    return { redirect: true, status: 301, url }
  }

  return { redirect: false }
}
